<template>
  <HeaderComponent/>

  <div class="bg-gray-50">
    <section class="relative flex flex-col md:flex-row h-auto md:h-screen">
      <!-- Left Side: Hero Image -->
      <div class="w-full md:w-1/2 flex justify-center items-center p-8 md:p-0 overflow-clip relative">
        <img src="https://arrify.com/wp-content/uploads/2022/05/Business-growth-and-Branding-1.gif" alt="Hero Graphic"
             class="w-full max-w-md md:max-w-none h-auto object-contain">
      </div>
      <!-- Right Side: Hero Content -->
      <div class="w-full md:w-1/2 flex flex-col justify-center text-left p-8 md:p-16 bg-white">
        <h1 class="text-4xl font-bold mb-4 text-blue-800 font-sans">Helping Businesses Grow Better with</h1>
        <h1 class="text-4xl font-bold mb-4 text-blue-500">Salesforce & WordPress</h1>
        <p class="text-lg text-gray-700 mb-8">
          We are a small team providing Salesforce development and consulting services to small and medium-sized
          businesses across the globe.
        </p>
        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 mb-8">
          <a href="/contact" class="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
            Get Free Consultation
            <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
            </svg>
          </a>
          <a href="#learn-more" class="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-300">
            Our Services
            <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </a>
        </div>
        <div class="relative flex justify-center items-center mt-8 space-x-8">
          <!-- Salesforce Icon -->
          <div class="bg-white shadow-lg rounded-full p-3 animate-bounce-slow">
            <img src="https://arrify.com/wp-content/uploads/2022/05/Salesforce.com_logo.svg-1.png" alt="Salesforce"
                 class="h-12 w-12 md:h-16 md:w-16 object-contain">
          </div>
          <!-- Plus Icon -->
          <div class="text-2xl md:text-3xl text-gray-500 animate-pulse-slow">
            <i class="fa-solid fa-plus"></i>
          </div>
          <!-- WordPress Icon -->
          <div class="bg-white shadow-lg rounded-full p-3 animate-bounce-slow">
            <img src="https://s.w.org/style/images/about/WordPress-logotype-wmark.png" alt="WordPress"
                 class="h-12 w-12 md:h-16 md:w-16 object-contain">
          </div>
        </div>
      </div>
    </section>

    <!-- What We Do Section -->
    <section id="learn-more" class="bg-white py-16">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-semibold text-center mb-8">What We Do?</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-blue-500 pb-2">
              <i class="fas fa-rocket text-4xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">Salesforce Implementation</h3>
            <p class="text-gray-600 mb-4">Deploy solution on Salesforce as per your business need to improve ROI</p>
          </div>
          <a href="/services/salesforce-implementation" class="mt-4 inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-green-500 pb-2">
              <i class="fas fa-cogs text-4xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">Salesforce Setup & Configuration</h3>
            <p class="text-gray-600 mb-4">Configure and setup Salesforce for quick start as per requirement</p>
          </div>
          <a href="/services/salesforce-setup-configuration" class="mt-4 inline-flex items-center text-green-600 font-semibold hover:text-green-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-purple-500 pb-2">
              <i class="fas fa-code text-4xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">Salesforce Custom Development</h3>
            <p class="text-gray-600 mb-4">Develop and deploy Solution out of the box using Code</p>
          </div>
          <a href="/services/salesforce-custom-development" class="mt-4 inline-flex items-center text-purple-600 font-semibold hover:text-purple-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-yellow-500 pb-2">
              <i class="fas fa-plug text-4xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">Salesforce Integration</h3>
            <p class="text-gray-600 mb-4">Connect Salesforce to any third party system using API's</p>
          </div>
          <a href="/services/salesforce-integration" class="mt-4 inline-flex items-center text-yellow-600 font-semibold hover:text-yellow-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-red-500 pb-2">
              <i class="fas fa-stethoscope text-4xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">Salesforce Support & Maintenance</h3>
            <p class="text-gray-600 mb-4">Regular support and maintenance on deployed Solution</p>
          </div>
          <a href="/services/salesforce-support-maintenance" class="mt-4 inline-flex items-center text-red-600 font-semibold hover:text-red-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
        <div
            class="bg-gray-50 p-7 shadow-md rounded-md flex flex-col items-center text-center transition-transform duration-300 ease-in-out hover:scale-105 hover:bg-white">
          <div class="w-full flex-grow">
            <div class="flex-shrink-0 text-teal-500 pb-2">
              <i class="fa-brands fa-wordpress text-5xl"></i>
            </div>
            <h3 class="text-xl font-bold mb-2">WordPress Portal using Salesforce</h3>
            <p class="text-gray-600 mb-4">Design Customer/Client Portal in WordPress using Salesforce Data</p>
          </div>
          <a href="/services/wordpress-salesforce-portal" class="mt-4 inline-flex items-center text-teal-600 font-semibold hover:text-teal-800 transition-colors">
            Learn More
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </a>
        </div>
      </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="bg-gray-100 py-16">
      <div class="container mx-auto px-4 text-center">
        <h2 class="text-4xl font-semibold text-center mb-8">We create solutions that people love</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-xl">
          <div class="bg-white shadow-md rounded-lg px-6 py-8">
            <p class="text-4xl font-bold text-blue-500">10+</p>
            <p class="text-gray-600">Completed Projects</p>
          </div>
          <div class="bg-white shadow-md rounded-lg px-6 py-8">
            <p class="text-4xl font-bold text-blue-500">7+</p>
            <p class="text-gray-600">Years Experience</p>
          </div>
          <div class="bg-white shadow-md rounded-lg px-6 py-8">
            <p class="text-4xl font-bold text-blue-500">20+</p>
            <p class="text-gray-600">Happy Clients</p>
          </div>
          <div class="bg-white shadow-md rounded-lg px-6 py-8">
            <p class="text-4xl font-bold text-blue-500">4</p>
            <p class="text-gray-600">Team Members</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="bg-white py-16">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
        <h2 class="text-4xl font-semibold mb-4">What Our Clients Say</h2>
        <p class="text-xl text-gray-600">Don't just take our word for it - hear from businesses we've helped grow</p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <!-- Testimonial 1 -->
        <div class="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
          <div class="flex items-center mb-4">
            <div class="flex text-yellow-400">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
          </div>
          <p class="text-gray-700 mb-6 italic">"Arrify transformed our Salesforce implementation. Their team understood our needs perfectly and delivered a solution that increased our sales efficiency by 40%. Highly recommended!"</p>
          <div class="flex items-center">
            <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
              JD
            </div>
            <div>
              <p class="font-semibold text-gray-800">John Davis</p>
              <p class="text-sm text-gray-600">CEO, TechCorp Solutions</p>
            </div>
          </div>
        </div>

        <!-- Testimonial 2 -->
        <div class="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
          <div class="flex items-center mb-4">
            <div class="flex text-yellow-400">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
          </div>
          <p class="text-gray-700 mb-6 italic">"The WordPress-Salesforce integration Arrify built for us has been a game-changer. Our clients can now access their data seamlessly through our portal. Outstanding work!"</p>
          <div class="flex items-center">
            <div class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
              SM
            </div>
            <div>
              <p class="font-semibold text-gray-800">Sarah Mitchell</p>
              <p class="text-sm text-gray-600">Director, Global Ventures UK</p>
            </div>
          </div>
        </div>

        <!-- Testimonial 3 -->
        <div class="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
          <div class="flex items-center mb-4">
            <div class="flex text-yellow-400">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
          </div>
          <p class="text-gray-700 mb-6 italic">"Professional, responsive, and incredibly knowledgeable. Arrify's ongoing support has been invaluable. They're more than just vendors - they're true partners in our success."</p>
          <div class="flex items-center">
            <div class="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
              RP
            </div>
            <div>
              <p class="font-semibold text-gray-800">Robert Patterson</p>
              <p class="text-sm text-gray-600">VP Operations, Innovation Labs AU</p>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA after testimonials -->
      <div class="text-center mt-12">
        <p class="text-xl text-gray-700 mb-6">Join our growing list of satisfied clients</p>
        <a href="/contact" class="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
          Start Your Project Today
          <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
          </svg>
        </a>
      </div>
      </div>
    </section>

    <!-- How We Work Section - Mobile -->
    <section class="bg-gray-50 py-16 md:hidden">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-semibold text-center mb-8">How We Work</h2>

      <div class="grid grid-cols-1 gap-12">
        <div class="bg-white p-8 shadow-md rounded-md hover:shadow-lg transition-shadow duration-300 flex items-start">
          <div class="mr-8">
            <div
                class="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center text-white text-3xl font-bold">
              1
            </div>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4 flex items-center">
              <i class="fas fa-lightbulb text-blue-500 mr-3 text-4xl"></i>
              Requirement
            </h3>
            <p class="text-gray-600 leading-relaxed">We understand your business and challenges to provide the best
              solution.</p>
          </div>
        </div>
        <div class="bg-white p-8 shadow-md rounded-md hover:shadow-lg transition-shadow duration-300 flex items-start">
          <div class="mr-8">
            <div
                class="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center text-white text-3xl font-bold">
              2
            </div>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4 flex items-center">
              <i class="fas fa-desktop text-blue-500 mr-3 text-4xl"></i>
              Designing
            </h3>
            <p class="text-gray-600 leading-relaxed">We suggest a minimum viable solution based on your requirements and
              timeline.</p>
          </div>
        </div>
        <div class="bg-white p-8 shadow-md rounded-md hover:shadow-lg transition-shadow duration-300 flex items-start">
          <div class="mr-8">
            <div
                class="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center text-white text-3xl font-bold">
              3
            </div>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4 flex items-center">
              <i class="fas fa-code text-blue-500 mr-3 text-4xl"></i>
              Implement
            </h3>
            <p class="text-gray-600 leading-relaxed">We implement the suggested solution, including development,
              testing, and deployment.</p>
          </div>
        </div>
        <div class="bg-white p-8 shadow-md rounded-md hover:shadow-lg transition-shadow duration-300 flex items-start">
          <div class="mr-8">
            <div
                class="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center text-white text-3xl font-bold">
              4
            </div>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4 flex items-center">
              <i class="fas fa-comment-dots text-blue-500 mr-3 text-4xl"></i>
              Feedback
            </h3>
            <p class="text-gray-600 leading-relaxed">After deployment, we collect feedback and fix issues and bugs.</p>
          </div>
        </div>
        <div class="bg-white p-8 shadow-md rounded-md hover:shadow-lg transition-shadow duration-300 flex items-start">
          <div class="mr-8">
            <div
                class="bg-blue-500 rounded-full w-16 h-16 flex items-center justify-center text-white text-3xl font-bold">
              5
            </div>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4 flex items-center">
              <i class="fas fa-redo-alt text-blue-500 mr-3 text-4xl"></i>
              Iterate
            </h3>
            <p class="text-gray-600 leading-relaxed">We repeat the process until you get your optimum solution.</p>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- How We Work Section - Desktop -->
    <section class="bg-gray-50 py-16 mb-10 sm:hidden md:block">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-semibold text-center mb-20">How We Work</h2>
      <div class="relative">
        <!-- Vertical line -->
        <div class="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-300"></div>

        <!-- Work steps -->
        <div class="space-y-24">
          <!-- Requirement -->
          <div class="flex items-center">
            <div class="w-1/2 pr-8 text-right">
              <h3 class="text-xl font-bold mb-4">Requirement</h3>
              <p class="text-gray-600 leading-relaxed">We understood the business and challenges that you are facing to
                provide the best solution.</p>
            </div>
            <div
                class="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-red-500 border-4 border-white flex items-center justify-center">
              <i class="fas fa-edit text-white text-2xl"></i>
            </div>
            <div class="w-1/2 pl-8"></div>
          </div>

          <!-- Designing -->
          <div class="flex items-center">
            <div class="w-1/2 pr-8"></div>
            <div
                class="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-yellow-500 border-4 border-white flex items-center justify-center">
              <i class="fas fa-palette text-white text-2xl"></i>
            </div>
            <div class="w-1/2 pl-8">
              <h3 class="text-xl font-bold mb-4">Designing</h3>
              <p class="text-gray-600 leading-relaxed">We suggest the minimum valuable solution based on requirement and
                timeline. So that you can start using that as soon as possible.</p>
            </div>
          </div>

          <!-- Implement -->
          <div class="flex items-center">
            <div class="w-1/2 pr-8 text-right">
              <h3 class="text-xl font-bold mb-4">Implement</h3>
              <p class="text-gray-600 leading-relaxed">We implement the suggested solution. It includes development,
                testing and deployment.</p>
            </div>
            <div
                class="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-green-500 border-4 border-white flex items-center justify-center">
              <i class="fas fa-cloud-upload-alt text-white text-2xl"></i>
            </div>
            <div class="w-1/2 pl-8"></div>
          </div>

          <!-- Feedback -->
          <div class="flex items-center">
            <div class="w-1/2 pr-8"></div>
            <div
                class="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-blue-500 border-4 border-white flex items-center justify-center">
              <i class="fas fa-comments text-white text-2xl"></i>
            </div>
            <div class="w-1/2 pl-8">
              <h3 class="text-xl font-bold mb-4">Feedback</h3>
              <p class="text-gray-600 leading-relaxed">After deployment, we collect the feedback and fix issues and
                bugs.</p>
            </div>
          </div>

          <!-- Implement -->
          <div class="flex items-center">
            <div class="w-1/2 pr-8 text-right">
              <h3 class="text-xl font-bold mb-4">Iterate</h3>
              <p class="text-gray-600 leading-relaxed">We repeat the process until you get your optimum solution</p>
            </div>
            <div
                class="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-violet-500 border-4 border-white flex items-center justify-center">
              <i class="fas fa-sync text-white text-2xl"></i>
            </div>
            <div class="w-1/2 pl-8"></div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Recent Posts Section -->
    <section class="bg-white py-16">
      <div class="container mx-auto px-4">
        <client-only>
          <h2 class="text-4xl font-semibold text-center mb-8">Recent Posts</h2>
        <!-- Use PostsGrid component -->
        <PostsGrid
          :posts="recentPosts"
          :currentPage="currentPage"
          :totalPages="totalPages"
          :isPaginationShow="showPagination"
          :isCategoryShow="showCategory"
        />
      </client-only>
      <!--<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="post in recentPosts" :key="post.link" class="bg-white shadow-md rounded-lg overflow-hidden">
          <div class="h-48 bg-gray-200 flex items-center justify-center">
            <img v-if="post.image_url" :src="post.image_url" :alt="post.title" class="w-full h-full object-cover">
            <svg v-else class="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold mb-2" v-html="decodeHtmlEntities(post.title)"></h3>
            <p class="text-gray-600 mb-4" v-html="post.excerpt"></p>
            <a :href="getRelativeLink(post.link)" class="text-blue-500 hover:underline">Read more</a>
          </div>
        </div>
      </div>-->
      </div>
    </section>

    <!-- Contact Form Section -->
    <section class="bg-gradient-to-br from-blue-600 to-blue-800 py-16">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto">
          <div class="text-center mb-12">
            <h2 class="text-4xl font-bold text-white mb-4">Ready to Transform Your Business?</h2>
            <p class="text-xl text-blue-100">Get a free consultation with our Salesforce experts. Let's discuss how we can help you grow.</p>
          </div>
          <div class="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <!-- Left Side: Form -->
              <div>
                <h3 class="text-2xl font-bold text-gray-800 mb-6">Send us a message</h3>
                <client-only>
                  <ContactForm :formId="4" />
                </client-only>
              </div>
              <!-- Right Side: Benefits -->
              <div class="bg-gray-50 rounded-xl p-6 lg:p-8">
                <h3 class="text-2xl font-bold text-gray-800 mb-6">What You'll Get</h3>
                <div class="space-y-4">
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div class="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="ml-4">
                      <h4 class="text-lg font-semibold text-gray-800">Free 30-Min Consultation</h4>
                      <p class="text-gray-600">Discuss your challenges and explore solutions with our experts</p>
                    </div>
                  </div>
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div class="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="ml-4">
                      <h4 class="text-lg font-semibold text-gray-800">Custom Solution Proposal</h4>
                      <p class="text-gray-600">Get a tailored plan that fits your business needs and budget</p>
                    </div>
                  </div>
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div class="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="ml-4">
                      <h4 class="text-lg font-semibold text-gray-800">Quick Response Time</h4>
                      <p class="text-gray-600">We'll get back to you within 24 hours on business days</p>
                    </div>
                  </div>
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div class="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600">
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="ml-4">
                      <h4 class="text-lg font-semibold text-gray-800">No Obligation</h4>
                      <p class="text-gray-600">Free consultation with no strings attached</p>
                    </div>
                  </div>
                </div>
                <div class="mt-8 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                  <p class="text-sm text-gray-700">
                    <strong class="text-blue-800">Trusted by 20+ businesses</strong> across US, UK, and Australia
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-gray-200 h-96">
      <iframe src="https://maps.google.com/maps?q=Arrify&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=&amp;output=embed"
              width="100%" height="100%" style="border:0;" allowfullscreen="" title="Arrify on Google Map"
              loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
    </section>

  </div>

  <FooterComponent/>
</template>

<script setup>
import HeaderComponent from "../components/Header.vue";
import FooterComponent from "../components/Footer.vue";
import ContactForm from "../components/ContactForm.vue";
import { useHead } from '@vueuse/head';
import PostsGrid from "../components/PostsGrid.vue";
import { getRecentPosts } from '@/api/wordpress';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { ref, onMounted } from 'vue';

const recentPosts = ref([]);
const showPagination = ref(false);
const showCategory = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);

onMounted(async () => {
  try {
    const response = await getRecentPosts();
    console.log(response);
    recentPosts.value = response.data;
  } catch (error) {
    console.error('Failed to fetch recent posts:', error);
  }
});

useHead({
  title: 'Salesforce Development Services in US, UK, AU - Arrify',
  meta: [
    { name: 'description', content: 'Providing Services ⭐ Salesforce PDF Generation ⭐ Flow Setup ⭐ Data Import/Export⭐ Development (Apex, Visualforce, LWC)⭐ Integration' },
    { name: 'robots', content: 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: 'Salesforce Development Services in US, UK, AU - Arrify' },
    { property: 'og:description', content: 'Providing Services ⭐ Salesforce PDF Generation ⭐ Flow Setup ⭐ Data Import/Export⭐ Development (Apex, Visualforce, LWC)⭐ Integration' },
    { property: 'og:url', content: 'https://arrify.com/' },
    { property: 'og:site_name', content: 'Arrify' },
    { property: 'og:updated_time', content: '2022-06-04T05:19:00+00:00' },
    { property: 'article:published_time', content: '2018-10-25T12:03:17+00:00' },
    { property: 'article:modified_time', content: '2022-06-04T05:19:00+00:00' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Salesforce Development Services in US, UK, AU - Arrify' },
    { name: 'twitter:description', content: 'Providing Services ⭐ Salesforce PDF Generation ⭐ Flow Setup ⭐ Data Import/Export⭐ Development (Apex, Visualforce, LWC)⭐ Integration' },
    { name: 'twitter:site', content: '@arrifyITS' },
    { name: 'twitter:creator', content: '@kritikgarg24' },
    { name: 'twitter:label1', content: 'Written by' },
    { name: 'twitter:data1', content: 'Kritik Garg' },
    { name: 'twitter:label2', content: 'Time to read' },
    { name: 'twitter:data2', content: 'Less than a minute' }
  ],
  link: [
    { rel: 'canonical', href: 'https://arrify.com/' }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: `{"@context":"https://schema.org","@graph":[{"@type":"Organization","@id":"https://arrify.com/#organization","name":"Arrify","url":"https://arrify.com","sameAs":["https://twitter.com/arrifyITS"],"logo":{"@type":"ImageObject","@id":"https://arrify.com/#logo","url":"https://arrify.com/wp-content/uploads/2018/12/cropped-Arrify_icon-copy.png","contentUrl":"https://arrify.com/wp-content/uploads/2018/12/cropped-Arrify_icon-copy.png","caption":"Arrify","inLanguage":"en-US","width":"512","height":"512"}},{"@type":"WebSite","@id":"https://arrify.com/#website","url":"https://arrify.com","name":"Arrify","publisher":{"@id":"https://arrify.com/#organization"},"inLanguage":"en-US","potentialAction":{"@type":"SearchAction","target":"https://arrify.com/?s={search_term_string}","query-input":"required name=search_term_string"}},{"@type":"ImageObject","@id":"https://arrify.com/wp-content/uploads/2022/05/Arrify_Text_small.png","url":"https://arrify.com/wp-content/uploads/2022/05/Arrify_Text_small.png","width":"375","height":"150","caption":"Arrify Logo","inLanguage":"en-US"},{"@type":"WebPage","@id":"https://arrify.com/#webpage","url":"https://arrify.com/","name":"Salesforce Development Services in US, UK, AU - Arrify","datePublished":"2018-10-25T12:03:17+00:00","dateModified":"2022-06-04T05:19:00+00:00","about":{"@id":"https://arrify.com/#organization"},"isPartOf":{"@id":"https://arrify.com/#website"},"primaryImageOfPage":{"@id":"https://arrify.com/wp-content/uploads/2022/05/Arrify_Text_small.png"},"inLanguage":"en-US"},{"@type":"Person","@id":"https://arrify.com/author/kritikgarg240194_h4lg9554/","name":"Kritik Garg","url":"https://arrify.com/author/kritikgarg240194_h4lg9554/","image":{"@type":"ImageObject","@id":"https://secure.gravatar.com/avatar/c463a7716eaebad8e164f62f35b28c84?s=96&amp;d=mm&amp;r=g","url":"https://secure.gravatar.com/avatar/c463a7716eaebad8e164f62f35b28c84?s=96&amp;d=mm&amp;r=g","caption":"Kritik Garg","inLanguage":"en-US"},"sameAs":["https://www.facebook.com/kritikgarg24","https://twitter.com/kritikgarg24"],"worksFor":{"@id":"https://arrify.com/#organization"}},{"@type":"Article","headline":"Salesforce Development Services in US, UK, AU - Arrify","keywords":"Salesforce Development,PDF Generation,Flow Setup","datePublished":"2018-10-25T12:03:17+00:00","dateModified":"2022-06-04T05:19:00+00:00","author":{"@id":"https://arrify.com/author/kritikgarg240194_h4lg9554/","name":"Kritik Garg"},"publisher":{"@id":"https://arrify.com/#organization"},"description":"Providing Services ⭐ Salesforce PDF Generation ⭐ Flow Setup ⭐ Data Import/Export⭐ Development (Apex, Visualforce, LWC)⭐ Integration","name":"Salesforce Development Services in US, UK, AU - Arrify","@id":"https://arrify.com/#richSnippet","isPartOf":{"@id":"https://arrify.com/#webpage"},"image":{"@id":"https://arrify.com/wp-content/uploads/2022/05/Arrify_Text_small.png"},"inLanguage":"en-US","mainEntityOfPage":{"@id":"https://arrify.com/#webpage"}}]}`
    }
  ]
});
</script>

<style scoped>
@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse-slow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-bounce-slow {
  animation: bounce-slow 3s infinite;
}

.animate-pulse-slow {
  animation: pulse-slow 2s infinite;
}
</style>
